import EventEmitter from 'events'
import { toast } from 'react-hot-toast'
import { ZegoExpressEngine } from 'zego-express-engine-webrtc'
import { ZegoSoundLevelInfo } from 'zego-express-engine-webrtc/sdk/code/zh/ZegoExpressEntity.web'
import ZegoLocalStream from 'zego-express-engine-webrtc/sdk/code/zh/ZegoLocalStream.web'
import { ZegoCaptureCamera, ZegoCaptureMicrophone, ZegoStreamOptions } from 'zego-express-engine-webrtc/sdk/src/common/zego.entity'

import { ActMediaType, ChangeCamera, ChangeMicrophone, PlayConf, RemoteUser } from '.'

export interface RtcOptions {
  channel: string
  uid: number
  token?: string
}

const zegoNet: Record<number, number> = {
  0: 1,
  1: 3,
  2: 5,
  3: 0
}

const cameraVideoTrackInitConfig = ({ video, audio }: { video: false | ZegoCaptureCamera; audio: false | ZegoCaptureMicrophone }): ZegoStreamOptions => ({
  camera: {
    video: !video
      ? false
      : {
          // input: '',
          quality: 4,
          width: 1280,
          height: 960,
          frameRate: 15,
          // "default" | "motion" | "detail"
          // motion: 不会降低帧率，但是可能会降低发送分辨率
          // detail: 不会降低发送分辨率，但是可能会降低帧率
          optimizationMode: 'detail',
          ...video
        },
    audio: !audio
      ? false
      : {
          // input: ''
          ...audio
        }
  },
  // 视频码率
  videoBitrate: 960
  // 音频码率
  // audioBitrate
})

export default class Zego extends EventEmitter {
  private appId: number
  private server: string
  private channel: string = ''
  private token: string = ''
  private uid: number = 0
  private mainUid: number = 0
  private viceUid: number = 0
  public localMainVideoTrack: ZegoLocalStream | undefined = undefined
  public localViceVideoTrack: ZegoLocalStream | undefined = undefined
  public remoteVideoTrack: RemoteUser[] = []
  public remoteAudioTrack: number[] = []
  public mainClient: ZegoExpressEngine | null = null
  // 设备ID
  public mainCameraDeviceId: string = ''
  public viceCameraDeviceId: string = ''
  public microphoneDeviceId: string = ''
  private local = {} // 网络
  private remote = {} // 网络

  constructor(appId: string, server: string) {
    super()
    this.appId = Number(appId)
    this.server = server
  }

  public async init(options: RtcOptions) {
    const { channel, token = '', uid } = options
    this.channel = channel
    this.token = token
    this.uid = uid
    this.mainUid = Number(`20${this.uid}`)
    this.viceUid = Number(`21${this.uid}`)
    this.mainClient = new ZegoExpressEngine(this.appId, this.server)
    // log
    this.mainClient.setLogConfig({ logLevel: 'error' })
    this.subscribeClientEvents(this.mainClient)
    await this.join(this.mainUid, this.mainClient, this.token)
  }

  subscribeClientEvents(client: ZegoExpressEngine) {
    // 开启音量变化
    client.setSoundLevelDelegate(true, 1000)
    // 推流/取消推流
    client.on('roomStreamUpdate', async (roomID, updateType, streamList, extendedData) => {
      console.log('roomStreamUpdate', roomID, updateType, streamList, extendedData)
      // 当 updateType 为 ADD 时，代表有音视频流新增，此时可以调用 startPlayingStream 接口拉取播放该音视频流
      if (updateType == 'ADD') {
        // 流新增，开始拉流
        // 这里为了使示例代码更加简洁，我们只拉取新增的音视频流列表中第的第一条流，在实际的业务中，建议开发者循环遍历 streamList ，拉取每一条音视频流
        for (let index = 0; index < streamList.length; index++) {
          const stream = streamList[index]
          const remoteStream = await client.startPlayingStream(stream.streamID)
          this.userPublished(Number(stream.streamID), remoteStream)
        }
      } else if (updateType == 'DELETE') {
        // 流删除，停止拉流
        for (let index = 0; index < streamList.length; index++) {
          const stream = streamList[index]
          client.stopPlayingStream(stream.streamID)
          this.userUnpublished(Number(stream.streamID))
        }
      }
    })
    // 推流/取消推流
    client.on('remoteMicStatusUpdate', async (streamID: string, status: 'OPEN' | 'MUTE') => {
      setTimeout(() => {
        if (status === 'OPEN') {
          super.emit('user-published', 'audio', { uid: Number(streamID) })
        } else {
          super.emit('user-unpublished', 'audio', { uid: Number(streamID) })
        }
      }, 1000)
    })
    // 音量变化
    client.on('soundLevelUpdate', this.volumeIndicator.bind(this))
    // 播放状态发生变化
    client.on('publisherStateUpdate', this.playerStatusChange.bind(this))
    // 加入频道/离开频道
    client.on('roomUserUpdate', (roomID, updateType, userList) => {
      if (updateType == 'ADD') {
        for (let i = 0; i < userList.length; i++) {
          // console.log(userList[i]['userID'], '加入了房间：', roomID)
          this.userJoined(Number(userList[i]['userID']))
        }
      } else if (updateType == 'DELETE') {
        for (let i = 0; i < userList.length; i++) {
          // console.log(userList[i]['userID'], '退出了房间：', roomID)
          this.userLeft(Number(userList[i]['userID']))
        }
      }
    })
    // 连接发生改变
    client.on('roomStateChanged', this.connectionStateShange.bind(this))
    // 远端网络
    client.on('playQualityUpdate', (streamID: string, playStats) => {
      // zego
      // videoQuality => 0,1,2,3分别代表优良中差
      // agora
      // 0 | 1 | 2 | 3 | 4 | 5 | 6; 分别代表断开,优,良好,一般,差,丢失,未知
      this.remote = {
        ...this.remote,
        [streamID]: {
          uplinkNetworkQuality: zegoNet[playStats.video.videoQuality],
          downlinkNetworkQuality: zegoNet[playStats.video.videoQuality]
        }
      }
      super.emit('network-quality', {
        local: this.local,
        remote: this.remote
      })
    })
    // 我的网络
    client.on('publishQualityUpdate', (_streamID: string, playStats) => {
      // console.log('publishQualityUpdate', playStats)
      // zego
      // videoQuality => 0,1,2,3分别代表优良中差
      // agora
      // 0 | 1 | 2 | 3 | 4 | 5 | 6; 分别代表断开,优,良好,一般,差,丢失,未知
      this.local = {
        uplinkNetworkQuality: zegoNet[playStats.video.videoQuality],
        downlinkNetworkQuality: zegoNet[playStats.video.videoQuality]
      }
      super.emit('network-quality', {
        local: this.local,
        remote: this.remote
      })
    })
    // 音视频开关
    // client.on('unmute-video', (e: { uid: UID }) => super.emit('unmute-video', 'video', { uid: e.uid }))
    // client.on('unmute-audio', (e: { uid: UID }) => super.emit('unmute-audio', 'audio', { uid: e.uid }))
    // client.on('mute-video', (e: { uid: UID }) => super.emit('mute-video', 'video', { uid: e.uid }, 2))
    // client.on('mute-audio', (e: { uid: UID }) => super.emit('mute-audio', 'audio', { uid: e.uid }))
  }

  public async join(uid: number, client: ZegoExpressEngine, token: string) {
    try {
      const res = await client.loginRoom(this.channel, token, { userID: this.mainUid.toString(), userName: this.mainUid.toString() }, { userUpdate: true })
      console.log('loginRoom', res)
      super.emit('join-success', uid)
    } catch (e) {
      super.emit('join-fail', e)
      return Promise.reject(e)
    }
  }

  public async publish(type: ActMediaType = 'all') {
    switch (type) {
      case 'mainVideo':
        if (this.localMainVideoTrack && this.mainClient) {
          this.mainClient.startPublishingStream(this.mainUid.toString(), this.localMainVideoTrack)
        }
        break
      case 'viceVideo':
        if (this.localViceVideoTrack && this.mainClient) {
          this.mainClient.startPublishingStream(this.viceUid.toString(), this.localViceVideoTrack)
        }
        break
      case 'audio':
        await this.deviceOn('audio')
        break
      default:
        if (this.localMainVideoTrack && this.mainClient) {
          this.mainClient.startPublishingStream(this.mainUid.toString(), this.localMainVideoTrack)
        }
        if (this.localViceVideoTrack && this.mainClient) {
          this.mainClient.startPublishingStream(this.viceUid.toString(), this.localViceVideoTrack)
        }
        break
    }
  }

  public async unpublish(type: ActMediaType = 'all') {
    switch (type) {
      case 'mainVideo':
        if (this.localMainVideoTrack && this.mainClient) {
          this.mainClient.stopPublishingStream(this.mainUid.toString())
        }
        break
      case 'viceVideo':
        if (this.localViceVideoTrack && this.mainClient) {
          this.mainClient.stopPublishingStream(this.viceUid.toString())
        }
        break
      case 'audio':
        await this.deviceOff('audio')
        break
      default:
        if (this.mainClient) {
          this.mainClient.stopPublishingStream(this.mainUid.toString())
          this.mainClient.stopPublishingStream(this.viceUid.toString())
        }
        break
    }
  }

  public async leave() {
    if (this.mainClient) {
      this.mainClient.logoutRoom(this.channel)
    }
  }

  // 销毁
  public async destroy() {
    // 声音取消订阅
    this.mainClient?.setSoundLevelDelegate(false)

    // 移除所有监听事件
    // this.mainClient?.removeAllListeners()

    // 销毁本地音视频轨道。
    this.mainClient?.stopPublishingStream(this.mainUid.toString())
    this.mainClient?.stopPublishingStream(this.viceUid.toString())

    // 销毁本地音视频轨道。
    this.localMainVideoTrack && this.mainClient?.destroyStream(this.localMainVideoTrack)
    this.localViceVideoTrack && this.mainClient?.destroyStream(this.localViceVideoTrack)

    // 清空数据
    this.localMainVideoTrack = undefined
    this.localViceVideoTrack = undefined
    this.remoteVideoTrack = []
    this.remoteAudioTrack = []

    this.local = {}
    this.remote = {}

    // 离开频道。
    await this.leave()

    this.mainClient?.destroyEngine()
    this.mainClient = null
  }

  async userPublished(streamID: number, mediaStream: MediaStream) {
    console.info(' --------- 连接远端流 --------- ', streamID, mediaStream)
    if (this.mainClient) {
      const remoteView = this.mainClient?.createRemoteStreamView(mediaStream)
      const remoteUser: RemoteUser = {
        uid: streamID,
        videoTrack: remoteView
      }
      this.remoteVideoTrack.push(remoteUser)
      this.remoteAudioTrack.push(remoteUser.uid)
      // tips: 在收到用户加入才初始化界面的情况下，收流回调需要延迟，避免监听错过
      setTimeout(() => {
        super.emit('user-published', 'video', remoteUser)
      }, 1000)
    }
  }

  async userUnpublished(streamID: number) {
    console.info(' --------- 远端流断开 --------- ', streamID)
    const audioIndex = this.remoteAudioTrack.findIndex((v) => v === streamID)
    if (audioIndex >= 0) {
      this.remoteAudioTrack.splice(audioIndex, 1)
    }
    const videoIndex = this.remoteVideoTrack.findIndex((v) => v.uid === streamID)
    if (videoIndex >= 0) {
      this.remoteVideoTrack.splice(videoIndex, 1)
    }

    super.emit('user-unpublished', 'video', { uid: streamID })
  }

  public async play(track: any, element: HTMLElement, config?: PlayConf, uid?: number) {
    const { fit = 'contain', mirror = true, type } = config || {}
    if (type === 'local') {
      track.playVideo(element, { objectFit: fit, mirror })
    } else {
      track.play(element, { objectFit: fit, mirror })
    }
  }

  volumeIndicator(streamList: ZegoSoundLevelInfo[]) {
    super.emit(
      'volume-indicator',
      streamList.map((v) => ({
        uid: Number(v.streamID),
        level: v.soundLevel
      }))
    )
  }

  playerStatusChange(result: { streamID: string; state: 'PUBLISHING' | 'NO_PUBLISH' | 'PUBLISH_REQUESTING'; errorCode: number; extendedData: string }) {
    // 推流状态更新回调
    const state = result['state']
    const streamID = result['streamID']
    const errorCode = result['errorCode']
    const extendedData = result['extendedData']
    if (state == 'PUBLISHING') {
      console.log('成功推送音视频流：', streamID)
    } else if (state == 'NO_PUBLISH') {
      console.log('未推送音视频流')
    } else if (state == 'PUBLISH_REQUESTING') {
      console.log('请求推送音视频流：', streamID)
    }
    console.log('错误码:', errorCode, ' 额外信息:', extendedData)
  }

  userJoined(_uid: number) {
    console.info('Zego userJoined', _uid)
    super.emit('user-add', _uid.toString())
  }

  userLeft(_uid: number) {
    console.info('Zego userLeft', _uid)
    super.emit('user-leave', _uid.toString())
  }

  connectionStateShange(_roomID: string, reason: string) {
    // 原始状态
    // let curState = ''
    // 当前状态
    let revState = ''
    // 状态变化原因
    // LEAVE
    // NETWORK_ERROR
    // SERVER_ERROR
    // UID_BANNED
    // IP_BANNED
    // CHANNEL_BANNED
    // FALLBACK
    // LICENSE_MISSING
    // LICENSE_EXPIRED
    // LICENSE_MINUTES_EXCEEDED
    // LICENSE_PERIOD_INVALID
    // LICENSE_MULTIPLE_SDK_SERVICE
    // LICENSE_ILLEGAL
    let reasons = ''

    if (reason == 'LOGINING') {
      console.log('[Zego] 登录中')
      revState = 'CONNECTING'
      reasons = 'LEAVE'
    } else if (reason == 'LOGINED') {
      console.log('[Zego] 登录成功')
      //只有当房间状态是登录成功或重连成功时，推流（startPublishingStream）、拉流（startPlayingStream）才能正常收发音视频
      //将自己的音视频流推送到 ZEGO 音视频云
      revState = 'CONNECTED'
      reasons = 'LEAVE'
    } else if (reason == 'LOGIN_FAILED') {
      console.log('[Zego] 登录失败')
      revState = 'DISCONNECTED'
      reasons = 'LEAVE'
    } else if (reason == 'RECONNECTING') {
      console.log('[Zego] 重连中')
      revState = 'RECONNECTING'
      reasons = 'NETWORK_ERROR'
    } else if (reason == 'RECONNECTED') {
      console.log('[Zego] 重连成功')
      revState = 'CONNECTED'
      reasons = 'NETWORK_ERROR'
    } else if (reason == 'RECONNECT_FAILED') {
      console.log('[Zego] 重连失败')
      revState = 'DISCONNECTED'
      reasons = 'NETWORK_ERROR'
    } else if (reason == 'KICKOUT') {
      console.log('被[Zego] 踢出房间')
      revState = 'DISCONNECTED'
      reasons = 'LEAVE'
    } else if (reason == 'LOGOUT') {
      console.log('[Zego] 登出成功')
      revState = 'DISCONNECTING'
      reasons = 'LEAVE'
    } else if (reason == 'LOGOUT_FAILED') {
      console.log('[Zego] 登出失败')
      revState = 'DISCONNECTED'
      reasons = 'LEAVE'
    }

    // SDK 和服务器有以下 5 种连接状态:
    //   1."DISCONNECTED": 连接断开。该状态表示用户处于以下任一阶段：
    //     尚未通过 join 加入频道。
    //     已经通过 leave 离开频道。
    //     被踢出频道或者连接失败等异常情况。
    //   2."CONNECTING": 正在连接中。当调用 join 时为此状态。
    //   3."CONNECTED": 已连接。该状态表示用户已经加入频道，可以在频道内发布或订阅媒体流。
    //   4."RECONNECTING": 正在重连中。因网络断开或切换而导致 SDK 与服务器的连接中断，SDK 会自动重连，此时连接状态变为 "RECONNECTING"。
    //   5."DISCONNECTING": 正在断开连接。在调用 leave 的时候为此状态。
    console.info('RTC connection-state', revState, revState, reasons)
    super.emit('connection-state', revState, revState, reasons)
  }

  async changeMainCamera({ deviceId, microphoneId, publish = true, status = 1 }: ChangeCamera) {
    console.info('ChangeMainCamera', deviceId, publish, status)
    try {
      this.mainCameraDeviceId = deviceId
      if (this.localMainVideoTrack) {
        if (deviceId) {
          await this.mainClient?.useVideoDevice(this.localMainVideoTrack, deviceId)
          toast.success('切换主摄像头成功')
        } else {
          // 即构没有单独取消音频的推流，所以就关闭即可
          await this.unpublish('audio')
          await this.unpublish('mainVideo')
          this.mainClient?.destroyStream(this.localMainVideoTrack)
          this.localMainVideoTrack = undefined
          super.emit('media-close', 'video', { uid: this.mainUid })
          super.emit('media-close', 'audio', { uid: this.mainUid })
          toast.success('关闭主摄像头成功')
        }
      } else if (deviceId) {
        if (deviceId) {
          this.localMainVideoTrack = await this.mainClient?.createZegoStream({
            ...cameraVideoTrackInitConfig({
              video: { input: deviceId },
              audio: { input: microphoneId }
            })
          })
          this.localMainVideoTrack?.takeStreamSnapshot()
          if (publish) {
            await this.publish('mainVideo')
            // 即构没有单独取消音频的推流，所以就开启即可
            await this.publish('audio')
          }
          super.emit('media-ready', 'video', { uid: this.mainUid, videoTrack: this.localMainVideoTrack })
          super.emit('media-ready', 'audio', { uid: this.mainUid })
        }
      }
    } catch (e) {
      console.info(e)
      // toast.error('切换主摄像头失败')
      return Promise.reject(e)
    }
  }

  async changeViceCamera({ deviceId, publish = true, status = 1 }: ChangeCamera) {
    console.info('ChangeViceCamera', deviceId, publish, status)
    try {
      this.viceCameraDeviceId = deviceId
      if (this.localViceVideoTrack) {
        if (deviceId) {
          await this.mainClient?.useVideoDevice(this.localViceVideoTrack, deviceId)
          toast.success('切换副摄像头成功')
        } else {
          await this.unpublish('viceVideo')
          this.mainClient?.destroyStream(this.localViceVideoTrack)
          this.localViceVideoTrack = undefined
          super.emit('media-close', 'video', { uid: this.viceUid })
          toast.success('关闭副摄像头成功')
        }
      } else if (deviceId) {
        if (deviceId) {
          this.localViceVideoTrack = await this.mainClient?.createZegoStream({
            ...cameraVideoTrackInitConfig({
              video: { input: deviceId },
              audio: false
            })
          })
          if (publish) {
            this.publish('viceVideo')
          }
          super.emit('media-ready', 'video', { uid: this.viceUid, videoTrack: this.localViceVideoTrack })
        }
      }
    } catch (e) {
      console.info(e)
      // toast.error('切换副摄像头失败')
      return Promise.reject(e)
    }
  }

  async changeMicrophone({ deviceId, publish = true }: ChangeMicrophone) {
    console.info('ChangeMicrophone', deviceId, publish)
    try {
      this.microphoneDeviceId = deviceId
      if (this.localMainVideoTrack) {
        if (deviceId) {
          await this.mainClient?.useAudioDevice(this.localMainVideoTrack, deviceId)
          super.emit('media-ready', 'audio', { uid: this.mainUid })
          toast.success('切换麦克风成功')
        } else {
          // 即构没有单独取消音频的推流，所以就关闭即可
          await this.unpublish('audio')
          super.emit('media-close', 'audio', { uid: this.mainUid })
          toast.success('关闭麦克风成功')
        }
      }
    } catch (e) {
      console.info(e)
      // toast.error('切换麦克风失败')
      return Promise.reject(e)
    }
  }

  async deviceOn(type: 'mainVideo' | 'viceVideo' | 'audio') {
    switch (type) {
      case 'mainVideo':
        if (!this.localMainVideoTrack) {
          return Promise.reject('摄像头为空，请查看设备')
        }
        if (this.mainClient) {
          this.mainClient.mutePublishStreamVideo(this.localMainVideoTrack, false, true)
        }
        super.emit('unmute-video', 'video', { uid: this.mainUid, videoTrack: this.localMainVideoTrack })
        break
      case 'viceVideo':
        if (!this.localViceVideoTrack) {
          return Promise.reject('摄像头为空，请查看设备')
        }
        if (this.mainClient) {
          this.mainClient.mutePublishStreamVideo(this.localViceVideoTrack, false, true)
        }
        super.emit('unmute-video', 'video', { uid: this.viceUid, videoTrack: this.localViceVideoTrack })
        break
      case 'audio':
        if (!this.localMainVideoTrack) {
          return Promise.reject('麦克风为空，请查看设备')
        }
        if (this.mainClient) {
          this.mainClient.mutePublishStreamAudio(this.localMainVideoTrack, false)
        }
        super.emit('unmute-audio', 'audio', { uid: this.mainUid })
        break
      default:
        break
    }
  }

  async deviceOff(type: 'mainVideo' | 'viceVideo' | 'audio') {
    switch (type) {
      case 'mainVideo':
        if (!this.localMainVideoTrack) {
          return Promise.reject('摄像头为空，请查看设备')
        }
        if (this.mainClient) {
          this.mainClient.mutePublishStreamVideo(this.localMainVideoTrack, true, false)
        }
        super.emit('mute-video', 'video', { uid: this.mainUid }, 2)
        break
      case 'viceVideo':
        if (!this.localViceVideoTrack) {
          return Promise.reject('摄像头为空，请查看设备')
        }
        if (this.mainClient) {
          this.mainClient.mutePublishStreamVideo(this.localViceVideoTrack, true, false)
        }
        super.emit('mute-video', 'video', { uid: this.viceUid }, 2)
        break
      case 'audio':
        if (!this.localMainVideoTrack) {
          return Promise.reject('麦克风为空，请查看设备')
        }
        if (this.mainClient) {
          this.mainClient.mutePublishStreamAudio(this.localMainVideoTrack, true)
        }
        super.emit('mute-audio', 'audio', { uid: this.mainUid })
        break
      default:
        break
    }
  }
}
